/**
 * Theme provider wrapper that applies MUI theme based on user preference.
 * Supports light, dark, and system modes.
 * CoOrdio Cortex theme — CoOrdio Design System.
 */

// React Imports
import { useEffect, useState, useMemo } from 'react';

// MUI Imports
import { ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material';

// Hook Imports
import { useSettings } from '../@core/hooks/useSettings';

// Type Imports
import type { SystemMode } from '../@core/types';

// Theme Imports
import theme from './index';

// Type Imports
import type { ReactNode } from 'react';

type ThemeProviderProps = {
  children: ReactNode;
};

/**
 * Theme provider component
 */
export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const { settings } = useSettings();
  const [systemMode, setSystemMode] = useState<SystemMode>('dark');

  /**
   * Determine if dark mode should be active based on mode setting.
   */
  useEffect(() => {
    if (settings.mode === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      setSystemMode(mediaQuery.matches ? 'dark' : 'light');

      const handleChange = (e: MediaQueryListEvent) => {
        setSystemMode(e.matches ? 'dark' : 'light');
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      setSystemMode(settings.mode === 'dark' ? 'dark' : 'light');
    }
  }, [settings.mode]);

  // Create theme
  const muiTheme = useMemo(() => {
    return theme(settings, systemMode, 'ltr');
  }, [settings, systemMode]);

  // Set data-theme attribute for CSS variables
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', systemMode);
  }, [systemMode]);

  // Set CSS variables for color channels
  useEffect(() => {
    const root = document.documentElement;
    
    // Set main color channels (Slate-based for modern look)
    root.style.setProperty('--mui-mainColorChannels-light', '15 23 42');
    root.style.setProperty('--mui-mainColorChannels-dark', '226 232 240');
    root.style.setProperty('--mui-mainColorChannels-lightShadow', '15 23 42');
    root.style.setProperty('--mui-mainColorChannels-darkShadow', '0 0 0');
    
    // Set color channel variables (new palette)
    root.style.setProperty('--mui-palette-primary-mainChannel', '139 92 246');
    root.style.setProperty('--mui-palette-secondary-mainChannel', '148 163 184');
    root.style.setProperty('--mui-palette-success-mainChannel', '52 211 153');
    root.style.setProperty('--mui-palette-error-mainChannel', '248 113 113');
    root.style.setProperty('--mui-palette-warning-mainChannel', '251 191 36');
    root.style.setProperty('--mui-palette-info-mainChannel', '34 211 238');
  }, []);

  return (
    <MuiThemeProvider theme={muiTheme} defaultMode={systemMode}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
};
